import { TimeCell } from '@/components/TimeCell';
import { useHandleActionItem } from '@/components/hooks/useHandleActionItem';
import { useCoreContext, useOpacity } from '@/core';
import { AccountsColumn, AccountsEntity } from '@/domain/entities/account';
import { EditOutlined, FilterOutlined } from '@ant-design/icons';
import { Button, DatePicker, Tag, Typography } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import dayjs, { Dayjs } from 'dayjs';
import { get } from 'lodash';
import React from 'react';
import { useWatch } from 'react-hook-form';

const { RangePicker } = DatePicker;

export const AccountTableColumns = () => {
  const { opacity, setOpacity } = useOpacity();
  const { handleClickEdit } = useHandleActionItem();
  const { control, setValue } = useCoreContext();
  const lastLoggedInFrom = useWatch({ control, name: 'lastLoggedInFrom' });
  const lastLoggedInTo = useWatch({ control, name: 'lastLoggedInTo' });

  const lastLoginRange: [Dayjs, Dayjs] | null =
    lastLoggedInFrom && lastLoggedInTo
      ? [dayjs(lastLoggedInFrom), dayjs(lastLoggedInTo)]
      : null;

  const clearLastLoginFilter = () => {
    setValue('lastLoggedInFrom', null);
    setValue('lastLoggedInTo', null);
  };

  const getLastLoginFilterProps = (): ColumnType<AccountsColumn> => ({
    // Visual indicator only — actual filtering is server-side via lastLoggedInFrom/To.
    filteredValue: lastLoginRange
      ? [String(lastLoginRange[0].valueOf()), String(lastLoginRange[1].valueOf())]
      : undefined,
    filterDropdown: ({ confirm }) => (
      <div
        className='p-2 flex flex-col gap-2'
        style={{ width: 280 }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={lastLoginRange}
          format='DD/MM/YYYY'
          className='w-full'
          onChange={(dates) => {
            if (!dates) {
              clearLastLoginFilter();
              confirm();
            } else if (dates[0] && dates[1]) {
              setValue('lastLoggedInFrom', dates[0].startOf('day').valueOf());
              setValue('lastLoggedInTo', dates[1].endOf('day').valueOf());
              confirm();
            }
          }}
        />
        {lastLoginRange && (
          <Button
            size='small'
            type='link'
            className='self-end !p-0'
            onClick={() => {
              clearLastLoginFilter();
              confirm();
            }}
          >
            Clear
          </Button>
        )}
      </div>
    ),
    filterIcon: () => (
      <FilterOutlined style={{ color: lastLoginRange ? '#1890ff' : undefined }} />
    ),
  });

  const tableColumns: ColumnsType<AccountsColumn> = [
    {
      title: 'Name',
      key: 'name',
      render: (record: AccountsEntity) => {
        return `${get(record, 'lastName', '')} ${get(record, 'firstName', '')}`;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      render: (record: any) => {
        if (Array.isArray(record) && record.length > 0) {
          return record.map((item: any) => {
            return (
              <div style={{ marginTop: '10px' }} key={item.id}>
                <Tag color='blue'>{item.name}</Tag>
              </div>
            );
          });
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      render: (value: boolean) => {
        if (value === true) {
          return <Typography.Text type='success'>Active</Typography.Text>;
        }
        return <Typography.Text disabled>Deactivated</Typography.Text>;
      },
    },
    {
      title: 'Last Login At',
      dataIndex: 'lastLoggedInAt',
      key: 'lastLoggedInAt',
      width: '180px',
      render: (value: string) => <TimeCell value={value} format='DD/MM/YYYY HH:mm:ss' />,
      ...getLastLoginFilterProps(),
    },
    {
      title: 'Action',
      width: '6rem',
      key: 'action',
      render: (record) => {
        return (
          <div
            className='actions'
            onMouseOver={() => setOpacity(1)}
            onMouseLeave={() => setOpacity(0.6)}
          >
            <EditOutlined
              style={{ color: 'blue', cursor: 'pointer', opacity }}
              onClick={() => handleClickEdit(record)}
            />
          </div>
        );
      },
      fixed: 'right',
    },
  ];

  return tableColumns;
};
